import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { postLoc } from "../components/NewsSection";
import { dateLocale } from "../content/i18n_extra";

// Japon Bilimi yazılarının sonuna eklenen "Bilim İnsanı Künyesi" kartları (koleksiyon hissi)
const SCIENTISTS = [
  {
    match: /yamanaka|ipsc|cira/i,
    kanji: "幹",
    name: "Prof. Shinya Yamanaka",
    org_tr: "Kyoto Üniversitesi · CiRA (iPSC Hücre Araştırmaları Merkezi)",
    org_en: "Kyoto University · CiRA (Center for iPS Cell Research)",
    feat_tr: "Nobel Tıp Ödülü, 2012 — yetişkin hücreyi kök hücreye geri döndüren iPSC keşfi",
    feat_en: "Nobel Prize in Medicine, 2012 — the iPSC discovery that rewinds adult cells",
  },
  {
    match: /honjo|pd-1|nivolumab/i,
    kanji: "免",
    name: "Prof. Tasuku Honjo",
    org_tr: "Kyoto Üniversitesi · İmmünoloji",
    org_en: "Kyoto University · Immunology",
    feat_tr: "Nobel Tıp Ödülü, 2018 — PD-1: bağışıklığın frenini çözen keşif",
    feat_en: "Nobel Prize in Medicine, 2018 — PD-1: releasing the immune system's brake",
  },
  {
    match: /ohsumi|otofaji|autophagy/i,
    kanji: "循",
    name: "Prof. Yoshinori Ohsumi",
    org_tr: "Tokyo Teknoloji Enstitüsü · Hücre Biyolojisi",
    org_en: "Tokyo Institute of Technology · Cell Biology",
    feat_tr: "Nobel Tıp Ödülü, 2016 — otofaji: hücrenin geri dönüşüm sistemi",
    feat_en: "Nobel Prize in Medicine, 2016 — autophagy: the cell's recycling system",
  },
  {
    match: /himac|a[gğ][iı]r iyon|karbon iyon/i,
    kanji: "束",
    name: "Dr. Hirohiko Tsujii",
    org_tr: "QST / NIRS · HIMAC (Chiba)",
    org_en: "QST / NIRS · HIMAC (Chiba)",
    feat_tr: "Ağır iyon tedavisinin öncüsü — 1994'ten beri HIMAC klinik programı",
    feat_en: "Pioneer of heavy-ion therapy — the HIMAC clinical programme since 1994",
  },
  {
    match: /bnct|nötron|notron/i,
    kanji: "中",
    name: "Prof. Koji Ono",
    org_tr: "KURRI · Kyoto Üniversitesi Araştırma Reaktörü Enstitüsü",
    org_en: "KURRI · Kyoto University Institute for Integrated Radiation",
    feat_tr: "BNCT klinik çalışmalarının öncüsü — bor nötron yakalama tedavisi",
    feat_en: "Pioneer of BNCT clinical research — boron neutron capture therapy",
  },
];
import { SpeakButton } from "../components/SpeakButton";
import { LinkedBody } from "../lib/linkify.jsx";

export default function NewsDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState({ posts: [], treatments: [] });
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setPost(null);
    setMissing(false);
    api.get(`/posts/${id}`).then((r) => setPost(r.data)).catch(() => setMissing(true));
    api.get(`/posts/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]);

  return (
    <div className="min-h-screen bg-white" data-testid="news-detail-page">
      <Navbar />
      <div className="pt-28 pb-24 px-6 max-w-3xl mx-auto">
        <Link to="/haberler" data-testid="news-detail-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sub hover:text-brand transition-colors">
          <ArrowLeft size={15} /> {t("nav_news")}
        </Link>
        {missing && <p className="mt-16 text-center text-sub">—</p>}
        {post && (
          <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-8">
            <SEO
              title={postLoc(post, lang, "title")}
              description={postLoc(post, lang, "excerpt") || postLoc(post, lang, "title")}
              path={`/haberler/${post.id}`}
              image={post.image_url || "/og-cover.jpg"}
              type="article"
              jsonLd={{
                "@context": "https://schema.org",
                "@type": "NewsArticle",
                headline: postLoc(post, lang, "title"),
                description: postLoc(post, lang, "excerpt"),
                image: post.image_url ? `https://japonyadakansertedavisi.com${post.image_url}` : undefined,
                datePublished: post.created_at,
                author: { "@type": "Organization", name: "2D Healthcare" },
                publisher: { "@type": "Organization", name: "2D Healthcare" },
              }}
            />
            <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase">
              {post.tag && <span className="px-2.5 py-1 rounded-full bg-brand/10 text-brand">{post.tag}</span>}
              <span className="text-sub">{new Date(post.created_at).toLocaleDateString(dateLocale(lang), { day: "numeric", month: "long", year: "numeric" })}</span>
              <SpeakButton
                getText={() => `${postLoc(post, lang, "title")}. ${postLoc(post, lang, "content")}`}
                label={t("tudi_listen_article")}
                testid="article-speak"
              />
            </div>
            <h1 className="mt-4 font-heading text-3xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{postLoc(post, lang, "title")}</h1>
            {postLoc(post, lang, "excerpt") && <p className="mt-5 text-lg text-sub leading-relaxed">{postLoc(post, lang, "excerpt")}</p>}
            {post.video_url && (
              <div className="mt-8" data-testid="news-tudi-video">
                <div className="text-[10px] font-bold tracking-widest uppercase text-brand mb-2.5">{t("news_tudi_badge")}</div>
                <video controls playsInline preload="none" poster={post.video_url.replace(".mp4", "-poster.jpg")} className="w-full rounded-3xl border border-black/5">
                  <source src={post.video_url.replace(".mp4", ".webm")} type="video/webm" />
                  <source src={post.video_url} type="video/mp4" />
                </video>
              </div>
            )}
            {post.image_url && <img src={post.image_url} alt={postLoc(post, lang, "title")} onError={(e) => { e.currentTarget.style.display = "none"; }} className="mt-8 w-full rounded-3xl border border-black/5" />}
            <div className="mt-8 text-base text-ink/75 leading-relaxed" data-testid="news-detail-content"><LinkedBody text={postLoc(post, lang, "content")} className="mb-5" /></div>
            {(() => {
              const sci = SCIENTISTS.find((s) => s.match.test(`${post.title_tr || ""} ${post.content_tr || ""}`));
              if (!sci) return null;
              return (
                <div className="mt-10 relative overflow-hidden rounded-3xl border border-[#D4AF37]/35 bg-gradient-to-br from-[#1A0608] via-[#2B0A0E] to-[#3A0303] p-6 sm:p-8" data-testid="scientist-card">
                  <span className="absolute -right-4 -bottom-8 text-[120px] leading-none font-light text-[#D4AF37]/10 select-none">{sci.kanji}</span>
                  <div className="text-[9px] font-heading font-bold tracking-[0.35em] uppercase text-[#D4AF37] mb-3">Bilim İnsanı Künyesi</div>
                  <div className="font-heading text-xl sm:text-2xl font-bold text-[#F7F1E3]">{sci.name}</div>
                  <div className="mt-1.5 text-[13px] text-[#E9CF8A]/90 font-heading italic">{lang === "en" ? sci.org_en : sci.org_tr}</div>
                  <div className="mt-3 h-px w-16 bg-gradient-to-r from-[#D4AF37]/70 to-transparent" />
                  <div className="mt-3 text-[13px] leading-relaxed text-[#EFE6D0]/85">{lang === "en" ? sci.feat_en : sci.feat_tr}</div>
                </div>
              );
            })()}
            <div className="mt-10 pt-6 border-t border-black/5 flex flex-wrap items-center gap-3" data-testid="news-share-bar">
              <span className="text-xs font-heading font-bold tracking-[0.2em] uppercase text-sub">{t("share_label")}</span>
              {[
                { n: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(postLoc(post, lang, "title") + " — https://japonyadakansertedavisi.com/haberler/" + post.id)}`, cls: "bg-[#25D366] text-white" },
                { n: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(postLoc(post, lang, "title"))}&url=${encodeURIComponent("https://japonyadakansertedavisi.com/haberler/" + post.id)}`, cls: "bg-ink text-white" },
                { n: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://japonyadakansertedavisi.com/haberler/" + post.id)}`, cls: "bg-[#0A66C2] text-white" },
              ].map((s) => (
                <a key={s.n} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`share-${s.n.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-xs font-bold ${s.cls} hover:opacity-85 transition-opacity`}>{s.n}</a>
              ))}
            </div>
            {(related.posts.length > 0 || related.treatments.length > 0) && (
              <nav className="mt-12 pt-8 border-t border-black/5" data-testid="related-content" aria-label="related">
                <div className="text-xs font-heading font-bold tracking-[0.2em] uppercase text-sub">{t("related_title")}</div>
                <div className="mt-4 grid sm:grid-cols-3 gap-3">
                  {related.posts.map((rp) => (
                    <Link key={rp.id} to={`/haberler/${rp.id}`} data-testid={`related-post-${rp.id.slice(0, 8)}`}
                      className="group bg-ice rounded-2xl p-4 border border-black/5 hover:border-brand/30 transition-colors">
                      {rp.tag && <span className="text-[10px] font-bold tracking-widest uppercase text-brand">{rp.tag}</span>}
                      <div className="mt-1.5 font-heading font-bold text-sm text-ink leading-snug group-hover:text-brand transition-colors">{postLoc(rp, lang, "title")}</div>
                    </Link>
                  ))}
                </div>
                {related.treatments.map((tr) => (
                  <Link key={tr} to={tr} data-testid="related-treatment"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline">
                    {t("related_treatment")} →
                  </Link>
                ))}
              </nav>
            )}
          </motion.article>
        )}
      </div>
      <Footer />
    </div>
  );
}
